import { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface SearchHeaderProps {
  search: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  isSearching?: boolean;
  searchType?: 'browse' | 'text' | 'image';
  onImageSearch?: (file: File) => void;
  onImageSearchClick?: () => Promise<boolean>;
}

export default function SearchHeader({
  search,
  onSearchChange,
  onSearchSubmit,
  isSearching = false,
  searchType = 'browse',
  onImageSearch,
  onImageSearchClick,
}: SearchHeaderProps) {
  const { t } = useTranslation('products');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const openModal = async () => {
    if (onImageSearchClick) {
      const allowed = await onImageSearchClick();
      if (!allowed) return;
    }
    setShowModal(true);
    setPreviewUrl(null);
    setSelectedFile(null);
    setIsDragging(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setPreviewUrl(null);
    setSelectedFile(null);
    setIsDragging(false);
  };

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
    e.target.value = '';
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleConfirmSearch = () => {
    if (selectedFile && onImageSearch) {
      onImageSearch(selectedFile);
      closeModal();
    }
  };

  return (
    <>
      <div className="w-full bg-white border-b border-ink-100">
        <div className="w-full px-4 md:px-6 lg:px-10 py-5 md:py-6">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Page Title */}
            <div className="hidden lg:block lg:ml-[180px]">
              <h1 className="text-lg font-semibold text-ink-700">
                {t('products.pageTitle') || 'Product Catalog'}
              </h1>
            </div>

            {/* Center/Right: Search Area */}
            <div className="flex flex-col sm:flex-row items-stretch gap-3 flex-1 lg:flex-none justify-end">
              {/* Visual Search Button */}
              <button
                onClick={openModal}
                disabled={isSearching && searchType === 'image'}
                className="hidden sm:inline-flex items-center justify-center gap-2.5 px-4 h-11 border-2 border-dashed border-warm-400 hover:border-warm-600 hover:bg-warm-100 text-warm-600 hover:text-warm-700 rounded-lg text-sm font-medium transition-all whitespace-nowrap group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-warm-500 text-white text-[10px] font-bold uppercase tracking-wider">
                  AI
                </span>
                {isSearching && searchType === 'image' ? (
                  <i className="ri-loader-4-line animate-spin text-base" />
                ) : (
                  <i className="ri-camera-lens-line text-base group-hover:scale-110 transition-transform" />
                )}
                <span>{t('products.search.imageSearch') || 'Search by Image'}</span>
              </button>

              {/* Divider */}
              <div className="hidden sm:block w-px h-11 bg-ink-300" />

              {/* Search Input */}
              <div className="flex-1 relative lg:w-80 xl:w-96">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 text-lg" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit()}
                  placeholder={t('products.search.placeholder') || 'Search products...'}
                  disabled={isSearching && searchType === 'text'}
                  className="w-full h-11 pl-10 pr-4 text-sm text-ink-700 placeholder:text-ink-300 bg-cream-50 border border-ink-200 rounded-md outline-none focus:border-warm-500 focus:ring-1 focus:ring-warm-500/20 transition-all disabled:opacity-60"
                />
              </div>

              {/* Search Button */}
              <button
                onClick={onSearchSubmit}
                disabled={isSearching && searchType === 'text'}
                className="inline-flex items-center justify-center gap-2 px-6 h-11 bg-ink-700 hover:bg-ink-800 text-white text-sm font-medium rounded-md transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching && searchType === 'text' ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-search-line" />
                )}
                <span>{t('products.search.button') || 'Search'}</span>
              </button>

              {/* Mobile Visual Search Button */}
              <button
                onClick={openModal}
                disabled={isSearching && searchType === 'image'}
                className="sm:hidden w-full inline-flex items-center justify-center gap-2.5 px-4 py-2.5 border-2 border-dashed border-warm-400 text-warm-600 text-sm rounded-lg hover:bg-warm-100 hover:border-warm-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-warm-500 text-white text-[10px] font-bold uppercase tracking-wider">
                  AI
                </span>
                {isSearching && searchType === 'image' ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-camera-lens-line" />
                )}
                {t('products.search.imageSearch') || 'Search by Image'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Search Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {!previewUrl ? (
              <>
                {/* Top: upload area */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`bg-ink-50 flex items-center justify-center py-14 md:py-16 cursor-pointer transition-colors ${
                    isDragging ? 'bg-warm-50' : ''
                  }`}
                >
                  <div className="w-20 h-20 rounded-2xl bg-white shadow-sm border border-ink-100 flex items-center justify-center">
                    <i className="ri-image-line text-4xl text-ink-300" />
                  </div>
                </div>

                {/* Description */}
                <div className="px-8 pt-6 pb-2 text-center">
                  <p className="text-sm text-ink-400">
                    {t('products.imageSearch.uploadDescription') ||
                      'Upload a reference image to find similar packaging.'}
                  </p>
                </div>

                {/* Bottom buttons */}
                <div className="px-8 py-6">
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center justify-center gap-2 h-12 rounded-lg border border-ink-200 hover:border-warm-500 hover:bg-warm-50 text-ink-700 hover:text-warm-700 text-sm font-medium transition-all"
                    >
                      <i className="ri-upload-line" />
                      <span>{t('products.imageSearch.upload') || 'Upload'}</span>
                    </button>
                    <button
                      onClick={() => cameraInputRef.current?.click()}
                      className="flex items-center justify-center gap-2 h-12 rounded-lg border border-ink-200 hover:border-ink-400 hover:bg-ink-50 text-ink-700 text-sm font-medium transition-all"
                    >
                      <i className="ri-camera-line" />
                      <span>{t('products.imageSearch.camera') || 'Camera'}</span>
                    </button>
                    <button
                      onClick={closeModal}
                      className="flex items-center justify-center gap-2 h-12 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 text-sm font-medium transition-all"
                    >
                      <i className="ri-close-line" />
                      <span>{t('products.imageSearch.cancel') || 'Cancel'}</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Top: preview area */}
                <div className="bg-ink-50 flex items-center justify-center p-6">
                  <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden shadow-sm border border-ink-100 bg-white">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                    <button
                      onClick={() => {
                        setPreviewUrl(null);
                        setSelectedFile(null);
                      }}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border border-ink-100 flex items-center justify-center text-ink-500 hover:text-red-500 transition-colors"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                </div>

                {/* Bottom buttons */}
                <div className="px-8 py-8">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setPreviewUrl(null);
                        setSelectedFile(null);
                      }}
                      className="flex items-center justify-center gap-2 h-12 rounded-lg border border-ink-200 hover:border-ink-300 hover:bg-ink-50 text-ink-700 text-sm font-medium transition-all"
                    >
                      <i className="ri-restart-line" />
                      <span>{t('products.imageSearch.reupload') || 'Re-upload'}</span>
                    </button>
                    <button
                      onClick={handleConfirmSearch}
                      disabled={!selectedFile || (isSearching && searchType === 'image')}
                      className="flex items-center justify-center gap-2 h-12 rounded-lg bg-ink-700 hover:bg-ink-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
                    >
                      {isSearching && searchType === 'image' ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-search-line" />
                      )}
                      <span>{t('products.imageSearch.searchButton') || 'Find Similar'}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
      />
    </>
  );
}